import React from "react"
import {
  Box, Center, Divider, Flex, SimpleGrid, Skeleton, Text,
} from "@chakra-ui/react"
import EventLinkDetails from "components/EventLink/EventLinkDetails"
import Spinner from "components/elements/Spinner"
import { useEventPageViews } from "queries/events"
import { DioboxEvent } from "sharedTypes"
import { CHART_COLORS } from "utilities/constants"
import DashboardCard, { EmptyState } from "../components/DashboardCard"
import PageViewsChart from "../components/PageViewsChart"
import { defaultRange } from "../activitySeries"

type ViewsTotalProps = { label: string, color: string, views?: number }

const ViewsTotal = ({ label, color, views }: ViewsTotalProps) => (
  <Box>
    <Flex align="center" gap={1.5} fontSize="xs" fontWeight="semibold" color="gray.600">
      <Box boxSize={2} borderRadius="full" bg={color} />
      {label}
    </Flex>
    {views === undefined ? (
      <Skeleton h={7} w={16} mt={1.5} mb={0.5} aria-label="Loading portal and form views" />
    ) : (
      <Text fontSize="2xl" fontWeight="semibold" mt={0.5}>{views.toLocaleString()}</Text>
    )}
    <Text fontSize="xs" color="gray.600">Total views</Text>
  </Box>
)

type Props = {
  event: DioboxEvent
  canEdit: boolean
  onPublish: () => void
  onShareLink: () => void
}

const PageViewsCard = ({
  event, canEdit, onPublish, onShareLink,
}: Props) => {
  const { data, isLoading } = useEventPageViews(
    event.id,
    defaultRange("daily", new Date(), event.timezone),
  )
  const maybeEmpty = !event.website.published && !event.website.viewsCount
  const noViews = maybeEmpty && Boolean(data)
    && !data?.webPage.length && !data?.portalAndForms?.length
  // An unpublished page only gets views through a link that's set to open it.
  const opensWebPage = event.rootTarget === "site"

  return (
    <DashboardCard title="Page Views">
      <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={2}>Event link</Text>
      <EventLinkDetails event={event} canEdit={canEdit} compact />
      <Divider my={5} />
      {maybeEmpty && isLoading && <Center py={6}><Spinner /></Center>}
      {noViews && (
        <EmptyState
          title="No page views yet"
          body={opensWebPage
            ? "Views appear once your event page is published."
            : "Views appear once guests open your event link."}
          cta={opensWebPage ? "Publish event page" : "Share event link"}
          onClick={canEdit ? (opensWebPage ? onPublish : onShareLink) : undefined}
        />
      )}
      {!noViews && !(maybeEmpty && isLoading) && (
        <>
          <SimpleGrid columns={2} spacing={4}>
            <ViewsTotal label="Web Page" color={CHART_COLORS[0]} views={event.website.viewsCount ?? 0} />
            {(isLoading || data?.portalAndFormsTotal !== undefined) && (
              <ViewsTotal label="Portal & Forms" color="purple.400" views={data?.portalAndFormsTotal} />
            )}
          </SimpleGrid>
          <Text fontSize="xs" fontWeight="semibold" color="gray.600" mt={5} mb={1}>Last 30 days</Text>
          <PageViewsChart eventId={event.id} timezone={event.timezone} />
        </>
      )}
    </DashboardCard>
  )
}

export default PageViewsCard
